import type { ScheduledShow, Show, User, UserSubscription } from '@shady-pines/db';
import { isNonNil, now, strongFromEntries, type Uuid } from '@shady-pines/types';
import { isEmpty, isNil } from 'lodash';
import type { StoreApi } from 'zustand';
import { supabaseApi } from '../api/api-store';
import type { ShowState } from './types';

export type ShowApi = ReturnType<typeof ShowApi>;
export const ShowApi = (
  get: StoreApi<ShowState>['getState'],
  set: StoreApi<ShowState>['setState'],
) =>
  ({
    fetchShows: async () => {
      const loading = get().loading.shows;

      if (loading) {
        return;
      }
      set(
        (s) =>
          ({
            loading: { ...s.loading, shows: true },
          }) as const satisfies Partial<ShowState>,
      );

      const { data: shows } = await supabaseApi()
        .from('shows_public')
        .select('*')
        .is('deleted_at', null)
        .order('start_time', { ascending: true });

      set(
        (s) =>
          ({
            shows: shows ?? [],
            lastShowRefresh: now(),
            loading: {
              ...s.loading,
              shows: false,
            },
          }) as const satisfies Partial<ShowState>,
      );
    },
    fetchShowSubscriptions: async (userId: Uuid<User> | null) => {
      const loading = get().loading.subscriptions;
      if (loading) {
        return;
      }

      if (isNil(userId)) {
        set({
          showSubscriptionsById: {},
        } as const satisfies Partial<ShowState>);
        return;
      }

      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              subscriptions: true,
            },
          }) as const satisfies Partial<ShowState>,
      );

      const { data: subscriptions } = await supabaseApi()
        .from('user_subscriptions')
        .select('*')
        .is('deleted_at', null)
        .eq('subscription_type', 'show')
        .eq('user_id', userId);

      const strongSubscriptions = (subscriptions ?? []) as UserSubscription<'show'>[];

      set(
        (s) =>
          ({
            showSubscriptionsById: strongFromEntries(
              strongSubscriptions.map((i) => [i.show_id, true]),
            ),
            loading: {
              ...s.loading,
              subscriptions: false,
            },
          }) as const satisfies Partial<ShowState>,
      );
    },
    updateShow: async (showId: ScheduledShow['id'], updates: Partial<ScheduledShow>) => {
      const updating = get().loading.updateShowById[showId];
      if (updating) {
        return;
      }

      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              updateShowById: {
                ...s.loading.updateShowById,
                [showId]: true,
              },
            },
          }) as const satisfies Partial<ShowState>,
      );

      const { data: updatedShow, error } = await supabaseApi()
        .from('shows')
        .update({
          updated_at: now(),
          ...updates,
        })
        .eq('id', showId)
        .select('*')
        .single();

      set((s) => {
        const shows = s.shows ?? [];
        const indexOfShow = shows.findIndex((show) => show.id === showId);

        if (indexOfShow > 0 && isNil(error)) {
          shows[indexOfShow] = {
            ...shows[indexOfShow],
            ...(updatedShow as ScheduledShow),
          };
        }

        return {
          loading: {
            ...s.loading,
            updateShowById: {
              ...s.loading.updateShowById,
              [showId]: false,
            },
          },
          shows,
        } as const satisfies Partial<ShowState>;
      });

      return error;
    },
    updateShowSubscription: async (userId: Uuid<User>, id: Show['id']) => {
      const loading = get().loading.updateSubscriptionsById[id];

      if (loading) {
        return;
      }

      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              updateSubscriptionsById: {
                ...s.loading,
                [id]: true,
              },
            },
          }) as const satisfies Partial<ShowState>,
      );

      const isSubscribed = get().showSubscriptionsById?.[id];

      const { error } = await (async () => {
        if (isSubscribed) {
          const { error: subscribeError } = await supabaseApi()
            .from('user_subscriptions')
            .update({
              updated_at: now(),
              deleted_at: now(),
            })
            .eq('user_id', userId)
            .eq('subscription_type', 'show')
            .eq('show_id', id);

          return { error: subscribeError };
        }
        const { error: subscribeError } = await supabaseApi().from('user_subscriptions').insert({
          show_id: id,
          user_id: userId,
          subscription_type: 'show',
        });

        return { error: subscribeError };
      })();

      if (isNil(error)) {
        set(
          (s) =>
            ({
              showSubscriptionsById: {
                ...s.showSubscriptionsById,
                [id]: !isSubscribed,
              },
            }) as const satisfies Partial<ShowState>,
        );
      }

      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              updateSubscriptionsById: {
                ...s.loading,
                [id]: false,
              },
            },
          }) as const satisfies Partial<ShowState>,
      );

      return error;
    },
    fetchSubscribersToMyShow: async (showId: Uuid<Show>) => {
      const alreadyFetched = isNonNil(get().myShowSubscriptions[showId]);

      if (alreadyFetched) {
        console.log('already fetched this shows subscribers');
        return;
      }

      const fetching = get().loading.fetchSubscribersToMyShow[showId];
      if (fetching) {
        console.log('fetching is in progress, returning early');
        return;
      }

      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              fetchSubscribersToMyShow: {
                ...s.loading.fetchSubscribersToMyShow,
                [showId]: true,
              },
            },
          }) as const satisfies Partial<ShowState>,
      );

      const subscribersToMyShow = await supabaseApi()
        .from('user_subscriptions')
        .select('*')
        .is('deleted_at', null)
        .eq('show_id', showId);

      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              fetchSubscribersToMyShow: {
                ...s.loading.fetchSubscribersToMyShow,
                [showId]: false,
              },
            },
            myShowSubscriptions: {
              ...s.myShowSubscriptions,
              [showId]: subscribersToMyShow.data ?? [],
            },
          }) as const satisfies Partial<ShowState>,
      );
    },

    refreshShows: async () => {
      const loading = get().loading.shows;

      if (loading) {
        return;
      }

      set(
        (s) =>
          ({
            loading: { ...s.loading, shows: true },
          }) as const satisfies Partial<ShowState>,
      );

      const lastShowRefresh = get().lastShowRefresh;
      const showIds = get().shows!.map((s) => s.id);

      // if the show count has changed, it means that a show has been activated or deactivated and we should just refetch everything
      const { count } = await supabaseApi()
        .from('shows_public')
        .select('*', { count: 'exact', head: true })
        .in('id', showIds);

      if (count !== showIds.length) {
        set(
          (s) =>
            ({
              loading: { ...s.loading, shows: false },
              lastShowRefresh: now(),
            }) as const satisfies Partial<ShowState>,
        );
        return {
          result: 'full-refresh-needed',
        } as const;
      }

      const { data: updatedShows } = await supabaseApi()
        .from('shows_public')
        .select('*')
        .is('deleted_at', null)
        .gt('updated_at', lastShowRefresh ?? now())
        .order('start_time', { ascending: true });

      if (isNil(updatedShows) || isEmpty(updatedShows)) {
        set(
          (s) =>
            ({
              loading: { ...s.loading, shows: false },
              lastShowRefresh: now(),
            }) as const satisfies Partial<ShowState>,
        );
        return {
          result: 'no-refresh-required',
        } as const;
      }

      set((s) => {
        const notUpdatedShows = s.shows!.filter(
          (show) => !updatedShows.some((u) => u.id === show.id),
        );
        return {
          loading: { ...s.loading, shows: false },
          shows: [...notUpdatedShows, ...updatedShows].sort((a, b) => a.start_time - b.start_time),
          lastShowRefresh: now(),
        } as const satisfies Partial<ShowState>;
      });

      return {
        result: 'refreshed',
        refreshedIds: updatedShows.map((u) => u.id),
      } as const;
    },
  }) as const;
